import {AppState, Auth0ProviderOptions} from "@auth0/auth0-react/src/auth0-provider.tsx";
import {Auth0ClientOptions} from "@auth0/auth0-spa-js";

const CODE_RE = /[?&]code=[^&]+/;
const STATE_RE = /[?&]state=[^&]+/;
const ERROR_RE = /[?&]error=[^&]+/;

class OAuthError extends Error {
    constructor(public error: string, public error_description?: string) {
        super(error_description || error);

        // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, OAuthError.prototype);
    }
}

export const deprecateRedirectUri = (options?: any) => {
    if (options?.redirectUri) {
        console.warn(
            'Using `redirectUri` has been deprecated, please use `authorizationParams.redirect_uri` instead as `redirectUri` will be no longer supported in a future version'
        );
        options.authorizationParams = options.authorizationParams || {};
        options.authorizationParams.redirect_uri = options.redirectUri;
        delete options.redirectUri;
    }

    if (options?.authorizationParams?.redirectUri) {
        console.warn(
            'Using `authorizationParams.redirectUri` has been deprecated, please use `authorizationParams.redirect_uri` instead as `authorizationParams.redirectUri` will be removed in a future version'
        );
        options.authorizationParams.redirect_uri =
            options.authorizationParams.redirectUri;
        delete options.authorizationParams.redirectUri;
    }
};

export const defaultOnRedirectCallback = (appState?: AppState): void => {
    window.history.replaceState(
        {},
        document.title,
        appState?.returnTo || window.location.pathname
    );
};


export const toAuth0ClientOptions = (
    opts: Auth0ProviderOptions
): Auth0ClientOptions => {
    deprecateRedirectUri(opts);

    return {
        ...opts,
        auth0Client: {
            name: 'auth0-react',
            version: '2.2.4',
        },
    };
};

export const hasAuthParams = (searchParams = window.location.search): boolean =>
    (CODE_RE.test(searchParams) || ERROR_RE.test(searchParams)) &&
    STATE_RE.test(searchParams);

export const initialAuthState = {
    isAuthenticated: false,
    isLoading: true,
};

const normalizeErrorFn =
    (fallbackMessage: string) =>
        (error: unknown): Error => {
            if (error instanceof Error) {
                return error;
            }
            // try to check errors of the following form: {error: string; error_description?: string}
            if (
                error !== null &&
                typeof error === 'object' &&
                'error' in error &&
                typeof error.error === 'string'
            ) {
                if (
                    'error_description' in error &&
                    typeof error.error_description === 'string'
                ) {
                    return new OAuthError(error.error, error.error_description);
                }
                return new OAuthError(error.error);
            }
            return new Error(fallbackMessage);
        };

export const loginError = normalizeErrorFn('Login failed');

export const tokenError = normalizeErrorFn('Get access token failed');

